import React from "react";
import { Repeat } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * RecurringExpenseFields — schlanke, wiederverwendbare Steuerung für wiederkehrende Ausgaben.
 *
 * Verhalten (bewusst minimal, damit das Modal nicht überladen wirkt):
 *   - Ein Haken "Wiederkehrende Ausgabe".
 *   - Erst nach Aktivierung erscheinen: Intervall-Buttons (Monatlich/Halbjährlich/Jährlich/Individuell),
 *     bei "Individuell" ein "alle N Wochen/Monate"-Feld, sowie ein optionales Enddatum.
 *
 * Controlled component:
 *   props.value  = { is_recurring, recurrence_interval, recurrence_custom_value, recurrence_custom_unit, recurrence_end_date }
 *   props.onChange(patch) → mergt patch in den Eltern-State
 */
const INTERVALS = [
  { key: "monthly", label: "Monatlich" },
  { key: "half_yearly", label: "Halbjährlich" },
  { key: "yearly", label: "Jährlich" },
  { key: "custom", label: "Individuell" },
];

export function RecurringExpenseFields({ value = {}, onChange, idPrefix = "recurring" }) {
  const isRecurring = !!value.is_recurring;
  const interval = value.recurrence_interval || null;
  const customValue = value.recurrence_custom_value ?? 2;
  const customUnit = value.recurrence_custom_unit || "months";
  const endDate = value.recurrence_end_date || "";

  const toggle = (checked) => {
    if (checked) {
      onChange({ is_recurring: true, recurrence_interval: interval || "monthly" });
    } else {
      onChange({ is_recurring: false });
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`${idPrefix}-section`}>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isRecurring}
          onChange={(e) => toggle(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300"
          data-testid={`${idPrefix}-checkbox`}
        />
        <Repeat className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-800">Wiederkehrende Ausgabe</span>
      </label>

      {isRecurring && (
        <div className="mt-3 space-y-3 pl-1" data-testid={`${idPrefix}-options`}>
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Wie oft?</div>
            <div className="flex flex-wrap gap-1.5">
              {INTERVALS.map(({ key, label }) => {
                const active = interval === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onChange({ recurrence_interval: key })}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`${idPrefix}-interval-${key}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {interval === "custom" && (
            <div className="flex items-center gap-2 text-sm" data-testid={`${idPrefix}-custom`}>
              <span className="text-slate-600">alle</span>
              <Input
                type="number"
                min={1}
                value={customValue}
                onChange={(e) => onChange({ recurrence_custom_value: e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value) || 1) })}
                className="h-8 w-16 text-sm"
                data-testid={`${idPrefix}-custom-value`}
              />
              <select
                value={customUnit}
                onChange={(e) => onChange({ recurrence_custom_unit: e.target.value })}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid={`${idPrefix}-custom-unit`}
              >
                <option value="weeks">Wochen</option>
                <option value="months">Monate</option>
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500" htmlFor={`${idPrefix}-end`}>Enddatum (optional)</label>
            <Input
              id={`${idPrefix}-end`}
              type="date"
              value={endDate}
              onChange={(e) => onChange({ recurrence_end_date: e.target.value || null })}
              className="h-8 w-40 text-sm"
              data-testid={`${idPrefix}-end-date`}
            />
          </div>

          <p className="text-[11px] text-slate-400 leading-snug">
            Folgebuchungen werden automatisch am Fälligkeitstag angelegt (Hinweis „wiederkehrend – kein neuer Beleg").
          </p>
        </div>
      )}
    </div>
  );
}

export default RecurringExpenseFields;
